// frontend/src/components/admin/CreateUser.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import {
    User,
    Mail,
    Lock,
    Phone,
    MapPin,
    ArrowLeft,
    Save,
    Upload,
    Eye,
    EyeOff
} from 'lucide-react';

const CreateUser = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        phone: '',
        address: '',
        avatar: null
    });
    const [previewUrl, setPreviewUrl] = useState(null);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        // Special handling for phone field to only allow digits
        if (name === 'phone') {
            const phoneRegex = /^[0-9]*$/;
            if (value === '' || phoneRegex.test(value)) {
                setFormData({ ...formData, phone: value });
            }
            return;
        }
        
        setFormData({ ...formData, [name]: value });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Size validation (5MB max)
            if (file.size > 5 * 1024 * 1024) {
                toast.error('Image size should be less than 5MB');
                return;
            }
            
            setFormData({ ...formData, avatar: file });
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validation
        if (!formData.name.trim()) {
            toast.error('Please enter name');
            return;
        }
        
        if (!formData.email.trim()) {
            toast.error('Please enter email');
            return;
        }
        
        if (!formData.password) {
            toast.error('Please enter password');
            return;
        }
        
        if (!formData.phone || formData.phone.length !== 10) {
            toast.error('Please enter a valid 10-digit phone number');
            return;
        }
        
        if (!formData.address.trim()) {
            toast.error('Please enter address');
            return;
        }
        
        setLoading(true);
        
        try {
            const userData = new FormData();
            
            // Append all form data
            Object.keys(formData).forEach(key => {
                if (key !== 'avatar' || formData[key] !== null) {
                    userData.append(key, formData[key]);
                }
            });
            
            const { data } = await axios.post('/api/v1/admin/user/new', userData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                withCredentials: true
            });
            
            if (data.success) {
                toast.success('User created successfully!');
                navigate('/admin/users');
            } else {
                toast.error(data.message || 'Failed to create user');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error creating user');
            console.error('Error creating user:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container mx-auto px-3 py-6">
            {/* Back to Users Button - Right aligned */}
            <div className="flex justify-end mb-4">
                <button
                    onClick={() => navigate('/admin/users')}
                    className="flex items-center gap-1.5 text-gray-600 hover:text-[#dc2626] transition-colors text-sm"
                >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back to All Users</span>
                </button>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-4 md:p-6">
                <h2 className="text-xl font-bold text-[#dc2626] mb-4 font-['Crimson_Text'] text-center">
                    Create New User
                </h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Profile Picture */}
                    <div className="flex flex-col items-center justify-center">
                        <div className="mb-3">
                            {previewUrl ? (
                                <img
                                    src={previewUrl}
                                    alt="Profile preview"
                                    className="h-24 w-24 rounded-full object-cover border-3 border-gray-200"
                                />
                            ) : (
                                <div className="h-24 w-24 rounded-full bg-gray-200 flex items-center justify-center">
                                    <User className="h-12 w-12 text-gray-400" />
                                </div>
                            )}
                        </div>
                        
                        <input
                            type="file"
                            id="avatar"
                            accept="image/*"
                            onChange={handleFileChange}
                            className="hidden"
                        />
                        
                        <label
                            htmlFor="avatar"
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#dc2626] cursor-pointer"
                        >
                            <Upload className="h-4 w-4 mr-1.5" />
                            {previewUrl ? 'Change Photo' : 'Upload Photo'}
                        </label>
                    </div>

                    {/* Name */}
                    <div className="relative">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Full Name
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-2.5 flex items-center pointer-events-none">
                                <User className="h-4 w-4 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] transition-colors text-sm text-center"
                                placeholder="Enter full name"
                                required
                            />
                        </div>
                    </div>

                    {/* Email */}
                    <div className="relative">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Email Address
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-2.5 flex items-center pointer-events-none">
                                <Mail className="h-4 w-4 text-gray-400" />
                            </div>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] transition-colors text-sm text-center"
                                placeholder="Enter email address"
                                required
                            />
                        </div>
                    </div>

                    {/* Password */}
                    <div className="relative">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Password
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-2.5 flex items-center pointer-events-none">
                                <Lock className="h-4 w-4 text-gray-400" />
                            </div>
                            <input
                                type={showPassword ? "text" : "password"}
                                name="password"
                                value={formData.password}
                                onChange={handleInputChange}
                                className="w-full pl-8 pr-8 py-1.5 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] transition-colors text-sm text-center"
                                placeholder="Enter password"
                                required
                            />
                            <button
                                type="button"
                                className="absolute inset-y-0 right-2.5 flex items-center"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? (
                                    <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                                ) : (
                                    <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Phone */}
                    <div className="relative">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Phone Number
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-2.5 flex items-center pointer-events-none">
                                <Phone className="h-4 w-4 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                                className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] transition-colors text-sm text-center"
                                placeholder="Enter 10-digit phone number"
                                maxLength="10"
                                required
                            />
                        </div>
                    </div>

                    {/* Address */}
                    <div className="relative">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Address
                        </label>
                        <div className="relative">
                            <div className="absolute top-2 left-2.5">
                                <MapPin className="h-4 w-4 text-gray-400" />
                            </div>
                            <textarea
                                name="address"
                                value={formData.address}
                                onChange={handleInputChange}
                                rows="3"
                                className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] transition-colors text-sm text-center"
                                placeholder="Enter full address"
                                required
                            />
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="flex justify-center mt-5">
                        <button
                            type="submit"
                            disabled={loading}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-[#dc2626] hover:bg-[#dc2626]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#dc2626] transition-colors disabled:opacity-50"
                        >
                            <Save className="w-4 h-4 mr-1.5" />
                            {loading ? 'Creating...' : 'Create User'}
                        </button>
                    </div>
                </form>
            </div>

            {/* Mobile Optimization Styles */}
            <style jsx>{`
                @media (max-width: 640px) {
                    .text-sm {
                        font-size: 0.75rem;
                        line-height: 1rem;
                    }
                    
                    .p-4 {
                        padding: 0.75rem;
                    }
                    
                    .space-y-4 > :not([hidden]) ~ :not([hidden]) {
                        --tw-space-y-reverse: 0;
                        margin-top: calc(0.75rem * calc(1 - var(--tw-space-y-reverse)));
                        margin-bottom: calc(0.75rem * var(--tw-space-y-reverse));
                    }
                    
                    .py-1\\.5 {
                        padding-top: 0.25rem;
                        padding-bottom: 0.25rem;
                    }
                    
                    .h-24 {
                        height: 5rem;
                    }
                    
                    .w-24 {
                        width: 5rem;
                    }
                    
                    .h-12 {
                        height: 2.5rem;
                    }
                    
                    .w-12 {
                        width: 2.5rem;
                    }
                }
            `}</style>
        </div>
    );
};

export default CreateUser;